import { useState } from 'react';
import { Mail, Linkedin, Instagram, Send, CheckCircle, AlertCircle, MapPin, Phone, Loader2 } from 'lucide-react';
import { z } from 'zod';
import { cvData } from '../../data/cv';
import { useInView } from '../../hooks/useInView';
import { supabase } from '@/integrations/supabase/client';

const ENQUIRY_TYPES = [
  'General Enquiry',
  'Job Opportunity',
  'Collaboration',
  'RotaCare / Project',
  'Other',
] as const;

const contactSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100, 'Max 100 characters'),
  email: z.string().trim().email('Please enter a valid email').max(255),
  subject: z.string().trim().max(200).optional(),
  message: z.string().trim().min(1, 'Message is required').max(2000, 'Max 2000 characters'),
  enquiry_type: z.string().min(1, 'Please select an enquiry type'),
  phone: z.string().trim().max(20).optional(),
  preferred_contact: z.enum(['Email', 'Phone']),
});

type FormData = z.infer<typeof contactSchema>;
type FieldErrors = Partial<Record<keyof FormData, string>>;

export const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    enquiry_type: '',
    phone: '',
    preferred_contact: 'Email',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [successName, setSuccessName] = useState('');
  const [successEmail, setSuccessEmail] = useState('');
  const [successContact, setSuccessContact] = useState('');

  const { ref: headRef, inView: headIn } = useInView();
  const { ref: leftRef, inView: leftIn } = useInView();
  const { ref: rightRef, inView: rightIn } = useInView();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name as keyof FormData]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleEnquiryType = (type: string) => {
    setFormData((prev) => ({ ...prev, enquiry_type: type }));
    if (fieldErrors.enquiry_type) setFieldErrors((prev) => ({ ...prev, enquiry_type: undefined }));
  };

  const handlePreferredContact = (method: 'Email' | 'Phone') => {
    setFormData((prev) => ({ ...prev, preferred_contact: method }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const errors: FieldErrors = {};
      result.error.errors.forEach((err) => {
        const key = err.path[0] as keyof FormData;
        if (!errors[key]) errors[key] = err.message;
      });
      setFieldErrors(errors);
      return;
    }

    setSubmitStatus('loading');

    const { error } = await supabase.from('contact_submissions').insert({
      name: result.data.name,
      email: result.data.email,
      subject: result.data.subject?.trim() || null,
      message: result.data.message,
      phone: result.data.phone?.trim() || null,
      enquiry_type: result.data.enquiry_type,
      preferred_contact: result.data.preferred_contact,
    } as never);

    if (error) {
      setSubmitStatus('error');
    } else {
      setSuccessName(result.data.name.split(' ')[0]);
      setSuccessEmail(result.data.email);
      setSuccessContact(result.data.preferred_contact);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '', enquiry_type: '', phone: '', preferred_contact: 'Email' });
    }

    setTimeout(() => setSubmitStatus('idle'), 8000);
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: cvData.personal.email, href: `mailto:${cvData.personal.email}` },
    { icon: MapPin, label: 'Location', value: cvData.personal.location, href: null },
    { icon: Linkedin, label: 'LinkedIn', value: 'dryeshjitesh', href: cvData.personal.linkedin },
    { icon: Instagram, label: 'Instagram', value: '@dryeshjitesh', href: cvData.personal.instagram },
  ];

  const inputClass = (hasError?: boolean) =>
    `w-full px-5 py-4 rounded-xl border ${hasError ? 'border-destructive ring-2 ring-destructive/20' : 'border-border focus:ring-2 focus:ring-primary/20 focus:border-primary'} bg-muted/30 text-foreground placeholder:text-muted-foreground focus:outline-none transition-all duration-300 text-sm hover:border-primary/30`;

  const messageLength = formData.message.length;

  return (
    <section id="contact" className="py-28 px-4 sm:px-6 lg:px-8 bg-background relative">
      <div className="max-w-6xl mx-auto">

        <div ref={headRef} className={`flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 mb-12 md:mb-16 transition-all duration-700 ${headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="shrink-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-accent mb-3">Get in Touch</p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Let&apos;s <span className="italic font-normal text-accent">connect.</span>
            </h2>
          </div>
          <div className="h-px w-full bg-border mb-3" />
        </div>


        <div className="grid md:grid-cols-5 gap-14 items-start">
          {/* Contact Info */}
          <div ref={leftRef} className={`md:col-span-2 space-y-8 transition-all duration-700 delay-100 ${leftIn ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <p className="text-muted-foreground leading-relaxed text-[15px]">
              I'm open to new opportunities and collaborations. Whether you're looking for a dedicated healthcare professional or want to discuss a project, feel free to reach out using the form.
            </p>
            <div className="space-y-5">
              {contactInfo.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/15 to-primary/5 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:from-primary/25 group-hover:to-primary/10 group-hover:shadow-md transition-all duration-300">
                    <Icon size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.1em] mb-1">{label}</p>
                    {href ? (
                      <a href={href} target="_blank" rel="noreferrer" className="text-sm text-foreground hover:text-primary transition-colors font-medium">
                        {value}
                      </a>
                    ) : (
                      <p className="text-sm text-foreground font-medium">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div ref={rightRef} className={`md:col-span-3 transition-all duration-700 delay-200 ${rightIn ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-card rounded-2xl p-10 shadow-elevated border border-border relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent/50 to-transparent" />
              
              <h3 className="text-xl font-bold text-foreground mb-8">Send me a Message</h3>

              {/* Success State */}
              {submitStatus === 'success' && (
                <div className="flex flex-col items-center gap-6 py-10">
                  {/* Icon */}
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary/15 to-primary/5 rounded-full flex items-center justify-center">
                      <CheckCircle size={36} className="text-primary" />
                    </div>
                    <span className="absolute -top-1 -right-1 w-7 h-7 gradient-premium rounded-full flex items-center justify-center text-primary-foreground text-xs font-bold shadow-lg">✓</span>
                  </div>

                  {/* Heading */}
                  <div className="text-center">
                    <h4 className="text-xl font-bold text-foreground mb-2">Message sent, {successName}!</h4>
                    <p className="text-sm text-muted-foreground">I'll review your message and get back to you shortly.</p>
                  </div>

                  {/* Info card */}
                  <div className="w-full bg-muted/50 border border-border rounded-xl p-5 space-y-4">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-muted-foreground uppercase tracking-[0.1em]">Sender</span>
                      <span className="font-medium text-foreground">{successName}</span>
                    </div>
                    <div className="w-full h-px bg-border" />
                    <div className="flex items-center justify-between text-xs gap-2">
                      <span className="font-semibold text-muted-foreground uppercase tracking-[0.1em] flex-shrink-0">Email</span>
                      <span className="font-medium text-foreground truncate">{successEmail}</span>
                    </div>
                    <div className="w-full h-px bg-border" />
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-muted-foreground uppercase tracking-[0.1em]">Preferred Response Via</span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/15 text-primary font-semibold">
                        {successContact === 'Email' ? '✉️' : '📞'} {successContact}
                      </span>
                    </div>
                    <div className="w-full h-px bg-border" />
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-muted-foreground uppercase tracking-[0.1em]">Status</span>
                      <span className="inline-flex items-center gap-1.5 text-primary font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                        Received
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="text-xs text-primary underline underline-offset-4 hover:opacity-70 transition-opacity"
                  >
                    Send another message
                  </button>
                </div>
              )}

              {submitStatus !== 'success' && (
                <form onSubmit={handleSubmit} noValidate className="space-y-6">
                  {/* Name + Email */}
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-2.5 uppercase tracking-[0.1em]">Name <span className="text-destructive">*</span></label>
                      <input name="name" value={formData.name} onChange={handleChange} placeholder="Your name" className={inputClass(!!fieldErrors.name)} maxLength={100} />
                      {fieldErrors.name && <p className="mt-1.5 text-xs text-destructive">{fieldErrors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-2.5 uppercase tracking-[0.1em]">Email <span className="text-destructive">*</span></label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" className={inputClass(!!fieldErrors.email)} maxLength={255} />
                      {fieldErrors.email && <p className="mt-1.5 text-xs text-destructive">{fieldErrors.email}</p>}
                    </div>
                  </div>

                  {/* Enquiry Type */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-2.5 uppercase tracking-[0.1em]">Enquiry Type <span className="text-destructive">*</span></label>
                    <div className="flex flex-wrap gap-2.5">
                      {ENQUIRY_TYPES.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => handleEnquiryType(type)}
                          className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 ${formData.enquiry_type === type
                            ? 'gradient-premium text-primary-foreground border-transparent shadow-card'
                            : 'bg-muted/30 text-foreground border-border hover:border-primary/30 hover:bg-primary/5'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                    {fieldErrors.enquiry_type && <p className="mt-1.5 text-xs text-destructive">{fieldErrors.enquiry_type}</p>}
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-2.5 uppercase tracking-[0.1em]">Subject</label>
                    <input name="subject" value={formData.subject} onChange={handleChange} placeholder="How can I help?" className={inputClass()} maxLength={200} />
                  </div>

                  {/* Phone + Preferred Contact */}
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-2.5 uppercase tracking-[0.1em]">
                        <span className="inline-flex items-center gap-1.5"><Phone size={11} /> Phone <span className="text-muted-foreground normal-case font-normal">(optional)</span></span>
                      </label>
                      <input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+44 7700 000000"
                        className={inputClass()}
                        maxLength={20}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-2.5 uppercase tracking-[0.1em]">Preferred Contact <span className="text-destructive">*</span></label>
                      <div className="flex gap-2.5 pt-0.5">
                        {(['Email', 'Phone'] as const).map((method) => (
                          <button
                            key={method}
                            type="button"
                            onClick={() => handlePreferredContact(method)}
                            className={`flex-1 py-3 rounded-xl text-xs font-semibold border transition-all duration-200 ${formData.preferred_contact === method
                              ? 'gradient-premium text-primary-foreground border-transparent shadow-card'
                              : 'bg-muted/30 text-foreground border-border hover:border-primary/30'
                            }`}
                          >
                            {method}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <label className="block text-xs font-semibold text-foreground uppercase tracking-[0.1em]">Message <span className="text-destructive">*</span></label>
                      <span className={`text-xs ${messageLength > 1800 ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {messageLength}/2000
                      </span>
                    </div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Your message..."
                      className={inputClass(!!fieldErrors.message)}
                      maxLength={2000}
                    />
                    {fieldErrors.message && <p className="mt-1.5 text-xs text-destructive">{fieldErrors.message}</p>}
                  </div>

                  {submitStatus === 'error' && (
                    <div className="flex items-center gap-2.5 text-sm text-destructive bg-destructive/10 px-5 py-4 rounded-xl border border-destructive/20">
                      <AlertCircle size={16} />
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitStatus === 'loading'}
                    className="group relative w-full flex items-center justify-center gap-2.5 px-6 py-4 gradient-premium text-primary-foreground rounded-xl font-semibold hover:opacity-95 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 shadow-hero hover:shadow-glow disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 overflow-hidden"
                  >
                    <span aria-hidden="true" className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-1000" />
                    <span className="relative flex items-center gap-2.5">
                    {submitStatus === 'loading' ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send Message
                      </>
                    )}
                    </span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
